<script setup>
import {
  ref, computed, watch, onMounted, markRaw, inject,
} from 'vue';
import { cloneDeep } from 'lodash';
import EditorSuma from './EditorSuma.vue';
import EditorMedia from './EditorMedia.vue';
import EditorPonderada from './EditorPonderada.vue';
import EditorSimple from './EditorSimple.vue';
import InputCheckboxBoolean from '../cifra/InputCheckboxBoolean.vue';

/**
 * Props del componente
 */
const props = defineProps({
  // Lista de objetos VariableFormula
  variablesDisponibles: { type: Array, required: true },
  modelValue: { type: Object, required: true },
});

/**
 * Emits del componente
 */
const emits = defineEmits(['update:modelValue', 'update:variablesDisponibles']);

/**
 * Composables
 */
const { t } = inject('I18n');

/**
 * Referencias del template
 */
const redondeoWrapper = ref();

/**
 * Estado reactivo
 */
const modelo = ref(cloneDeep(props.modelValue));
const variables = ref([...props.variablesDisponibles]);
const redondeoActivo = ref((modelo.value.redondeo || 0) > 0);
const redondeoInicial = ref(parseFloat(String(modelo.value.redondeo || 0.5)));
const usarCalificacionMayor = ref(modelo.value.usar_calificacion_mayor || false);

// Asegurar que el tipo tenga un valor por defecto
if (!modelo.value.tipo) {
  modelo.value.tipo = 'media';
}

/**
 * Mapeo de tipos de fórmula a componentes
 */
const vistasTipoFormula = markRaw({
  suma: EditorSuma,
  media: EditorMedia,
  ponderada: EditorPonderada,
  manual: EditorSimple,
});

/**
 * Tipos de fórmula disponibles
 */
const tiposFormula = Object.keys(vistasTipoFormula);

/**
 * Opciones para el tratamiento de blancos
 */
const tipoBlancos = computed(() => [
  {
    name: t('activerecord.enums.calificaciones/formula.tipo_blancos.no_calcular'),
    value: 'no_calcular',
  },
  {
    name: t('activerecord.enums.calificaciones/formula.tipo_blancos.como_ceros'),
    value: 'como_ceros',
  },
  {
    name: t('activerecord.enums.calificaciones/formula.tipo_blancos.excluir'),
    value: 'excluir',
  },
]);

/**
 * Clase CSS para el wrapper del redondeo
 */
const redondeoWrapperClass = computed(() => (!redondeoActivo.value ? 'hidden' : ''));

/**
 * Actualiza el valor del redondeo
 */
function setRedondeoValue(value) {
  modelo.value.redondeo = value ?? undefined;
}

/**
 * Inicializa el slider jQuery para el redondeo
 * @note Deuda técnica: Reemplazar con componente nativo de Vue o librería
 */
function initSlider() {
  /* global $ */
  if (!redondeoWrapper.value) return;

  const $wrapper = $(redondeoWrapper.value);
  $wrapper.find('.slider').each(function sliderInit() {
    const $slider = $(this);

    $slider.slider({
      min: 0,
      max: 1,
      step: 0.05,
      value: redondeoInicial.value,
      create(event) {
        $(event.target)
          .find('.ui-slider-handle')
          .append(
            `<span class="ui-slider-tooltip">${redondeoInicial.value
              .toString()
              .replace('.', ',')}</span>`,
          );
      },
      slide(_event, ui) {
        $slider
          .find('.ui-slider-tooltip')
          .text(String(ui.value).replace('.', ','));
        setRedondeoValue(ui.value);
      },
    });
  });
}

/**
 * Watchers
 */
watch(
  () => props.variablesDisponibles,
  (newVariables) => {
    variables.value = [...newVariables];
  },
);

watch(
  () => props.modelValue,
  (newModelValue) => {
    modelo.value = cloneDeep(newModelValue);
  },
);

watch(redondeoActivo, (activo) => {
  if (!activo) {
    setRedondeoValue(null);
  } else {
    setRedondeoValue(modelo.value.redondeo || redondeoInicial.value);
  }
});

watch(usarCalificacionMayor, (valor) => {
  modelo.value.usar_calificacion_mayor = valor;
});

/**
 * Lifecycle hooks
 */
onMounted(() => {
  if (redondeoActivo.value) {
    setRedondeoValue(modelo.value.redondeo);
  }
  initSlider();
});

/**
 * Obtiene los datos de la fórmula
 */
function getFormulaData() {
  return modelo.value;
}

/**
 * Valida la fórmula según su tipo
 */
function validar() {
  const data = getFormulaData();
  if (data.tipo === 'manual' && data.tipo_blancos === 'excluir') {
    throw new Error(t('tipo_blancos_excluir_no_valido_para_manual'));
  }
}

/**
 * Cambia el tipo de fórmula
 */
function cambiarTipoFormula(tipo) {
  // Si pasan a manual no hace falta limpiar, todas son válidas
  if (tipo === 'manual') {
    modelo.value.formula = props.modelValue.formula;
  } else {
    modelo.value.formula = '';
  }
  modelo.value.tipo = tipo;
  emits('update:modelValue', modelo.value);
}

/**
 * Elimina una variable disponible de la lista
 */
function eliminarVariableDisponible(variable) {
  const filtro = (v) => v.nombre_variable !== variable.nombre_variable;
  modelo.value.formula_variables = (
    modelo.value.formula_variables || []
  ).filter(filtro);
  emits('update:modelValue', modelo.value);
  variables.value = variables.value.filter(filtro);
  emits('update:variablesDisponibles', variables.value);
}

/**
 * Exponer métodos públicos al padre (template ref)
 */
defineExpose({
  getFormulaData,
  validar,
});
</script>

<template>
  <div>
    <div class="text-center m-b">
      <button
        v-for="tipo in tiposFormula"
        :key="tipo"
        type="button"
        class="btn m-r-xs"
        :class="{
          'btn-cifra': modelo.tipo === tipo,
          'btn-default': modelo.tipo !== tipo,
        }"
        @click="cambiarTipoFormula(tipo)"
      >
        {{ t(`enum_formula_tipo.${tipo}`) }}
      </button>
    </div>

    <component
      :is="vistasTipoFormula[modelo.tipo]"
      v-model="modelo"
      :variables-disponibles="variables"
      @eliminar-variable-disponible="eliminarVariableDisponible"
    />

    <div class="subtitulo-seccion">
      {{ t('Configuracion') }}
    </div>

    <div class="row m-b-lg">
      <div class="col-xs-6">
        <label class="control-label">
          {{ t('tratamiento_blancos') }}
        </label>
        <select v-model="modelo.tipo_blancos" class="form-control">
          <option
            v-for="tipo in tipoBlancos"
            :key="tipo.value"
            :value="tipo.value"
          >
            {{ tipo.name }}
          </option>
        </select>
      </div>
      <div class="col-xs-6">
        <InputCheckboxBoolean
          v-model="usarCalificacionMayor"
          tipo="switch"
          class="m-t-label"
        >
          {{ t('usar_calificacion_mayor') }}
        </InputCheckboxBoolean>
      </div>
    </div>

    <div class="row m-b-lg">
      <div class="col-xs-6">
        <InputCheckboxBoolean v-model="redondeoActivo" tipo="switch">
          {{ t('Redondeo_notas') }}
        </InputCheckboxBoolean>
      </div>
      <div
        ref="redondeoWrapper"
        class="col-xs-6 redondeo-wrapper"
        :class="redondeoWrapperClass"
      >
        <div class="wrapper-slider">
          <div class="redondeo slider"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Si el formulario principal está con form-horizontal bootstrap nos mete márgenes
.form-group {
  margin-left: 0;
  margin-right: 0;
}

.wrapper-slider {
  height: 40px;
}

.redondeo-wrapper {
  margin-top: -10px;
}
</style>
